/**
 * Billing — faturamento.
 * Mobile: hierarquia Contatos-like. Desktop intacto.
 */
import React, { useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { useAppContext } from '../../contexts/AppContext';
import { usePermissions } from '../../hooks/usePermissions';
import { PlanSummaryCard } from './components/PlanSummaryCard';
import { BillingInvoicesCard, BillingDetailsCard } from './components/BillingSidebar';
import ChangeLicensesModal from './components/ChangeLicensesModal';
import { PageSkeleton } from '../../components/ui';
import { PageHeader } from '../../components/layout/Header';
import { useBillingPage } from './hooks/useBillingPage';

const Billing: React.FC = () => {
    const { setCurrentPage } = useAppContext();
    const { canViewBilling } = usePermissions();

    useEffect(() => {
        if (!canViewBilling) {
            setCurrentPage('profile');
        }
    }, [canViewBilling, setCurrentPage]);

    const {
        isLoading,
        isPortalLoading,
        isUpdatingLicenses,
        customerName,
        planName,
        quantity,
        pricePerUnit,
        totalPrice,
        membersCount,
        trialInfo,
        canCancelSubscription,
        hasActiveSubscription,
        currentPeriodEnd,
        billingInfo,
        billingContact,
        dbLatestInvoice,
        isMembersLoading,
        isLicensesModalOpen,
        handleManageLicenses,
        handleCloseLicensesModal,
        handleUpdateLicenses,
        handleManageSubscription,
        openCustomerPortal,
    } = useBillingPage();

    if (!canViewBilling) {
        return null;
    }

    if (isLoading) {
        return <PageSkeleton variant="sidebar" mainCards={2} sideCards={2} />;
    }

    return (
        <div className="flex flex-col">
            <PageHeader
                title="Faturamento"
                subtitle="Gerencie sua assinatura, licenças e histórico de pagamentos."
                className="mb-0 gap-s3 md:mb-6 md:gap-3"
                titleClassName="text-lg md:text-xl"
                hideBackButton
            />

            <div className="grid grid-cols-1 gap-s4 pt-s3 md:gap-6 md:pt-0 lg:grid-cols-3">
                <div className="flex lg:col-span-2">
                    <PlanSummaryCard
                        className="h-full w-full"
                        customerName={customerName}
                        isTrialing={trialInfo.isTrialing}
                        planName={planName}
                        quantity={quantity}
                        usedLicenses={membersCount}
                        isMembersLoading={isMembersLoading}
                        pricePerUnit={pricePerUnit}
                        totalPrice={totalPrice}
                        hasActiveSubscription={hasActiveSubscription}
                        isPortalLoading={isPortalLoading}
                        onManageSubscription={handleManageSubscription}
                        onManageLicenses={handleManageLicenses}
                        trialDaysLeft={trialInfo.daysLeft}
                    />
                </div>

                <div className="flex lg:col-span-1">
                    <BillingInvoicesCard
                        billingInfo={billingInfo}
                        billingContact={billingContact}
                        hasActiveSubscription={hasActiveSubscription}
                        onOpenPortal={openCustomerPortal}
                        isTrialing={trialInfo.isTrialing}
                        latestInvoice={dbLatestInvoice}
                    />
                </div>

                <div className="flex lg:col-span-2">
                    <section aria-label="Cancelamento de assinatura" className="w-full">
                        <Card size="lg" className="flex h-full flex-col justify-center">
                            <Card.Header>
                                <Card.Title>Precisa cancelar sua assinatura?</Card.Title>
                            </Card.Header>
                            <Card.Content>
                                <p className="text-sm leading-relaxed text-secondary">
                                    Sabemos que, às vezes, as circunstâncias mudam. Antes de partir,
                                    você pode gerenciar sua assinatura diretamente no portal de pagamentos.
                                </p>
                                {canCancelSubscription ? (
                                    <button
                                        type="button"
                                        onClick={openCustomerPortal}
                                        disabled={isPortalLoading}
                                        className="mt-s4 text-sm font-semibold text-primary underline underline-offset-2 transition-colors hover:text-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {isPortalLoading ? 'Abrindo portal...' : 'Cancelar minha assinatura'}
                                    </button>
                                ) : null}
                            </Card.Content>
                        </Card>
                    </section>
                </div>

                <div className="flex lg:col-span-1">
                    <BillingDetailsCard
                        billingInfo={billingInfo}
                        billingContact={billingContact}
                        hasActiveSubscription={hasActiveSubscription}
                        onOpenPortal={openCustomerPortal}
                        isTrialing={trialInfo.isTrialing}
                        latestInvoice={dbLatestInvoice}
                    />
                </div>
            </div>

            <ChangeLicensesModal
                isOpen={isLicensesModalOpen}
                onClose={handleCloseLicensesModal}
                currentQuantity={quantity}
                licensesInUse={membersCount}
                pricePerLicense={pricePerUnit}
                planName={planName}
                currentPeriodEnd={currentPeriodEnd}
                onConfirm={handleUpdateLicenses}
                isLoading={isUpdatingLicenses}
                minQuantity={1}
            />
        </div>
    );
};

export default Billing;
